/**
 * WebhookSecret — HMAC signing secret + verification snippet for buyers.
 *
 * Mounted alongside the existing WebhookHealth card in /dashboard/delivery.
 * Buyers can reveal, copy and rotate their signing secret, and copy a tested
 * Python/Node verification snippet straight from the verification-docs API.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Eye, EyeOff, Copy, RefreshCw, Loader2, Terminal, Lock,
} from "lucide-react";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";

const LANGS = [
  { id: "python", label: "Python" },
  { id: "node",   label: "Node.js" },
];

export default function WebhookSecret() {
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState("python");

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/client/webhook/secret"),
        api.get("/webhooks/verification-docs"),
      ]);
      setData(r1.data);
      setDocs(r2.data);
    } catch (e) {
      // Silently ignore — buyer might not have a profile yet
    }
  };
  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (!window.confirm("Rotating will invalidate the current secret. Any in-flight webhook verifications will fail until you update your verifier. Continue?")) return;
    setBusy(true);
    try {
      const r = await api.post("/client/webhook/secret/rotate");
      setData((p) => ({ ...p, secret: r.data.secret, secret_prefix: r.data.secret.slice(0, 14) + "…" }));
      toast.success("Signing secret rotated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed");
    } finally { setBusy(false); }
  };

  if (!data) return null;

  const snippet = lang === "python" ? docs?.snippet_python : docs?.snippet_node;

  return (
    <Card className="rounded-2xl border-slate-200 shadow-soft" data-testid="webhook-secret-card">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/10 text-[#0055FF]">
            <ShieldCheck className="size-5" strokeWidth={1.7} />
          </span>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 text-[15px] tracking-tight">Signing secret</h3>
            <p className="text-[12.5px] text-slate-500 mt-0.5">
              Every webhook is signed with HMAC-SHA256. Verify the <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">{data.signature_header}</code> header on your end to defeat spoofing.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 flex items-center gap-3">
          <Lock className="size-4 text-slate-400" />
          <code className="flex-1 font-mono text-[12.5px] text-slate-700 truncate" data-testid="webhook-secret-display">
            {reveal ? data.secret : data.secret_prefix}
          </code>
          <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => setReveal((v) => !v)} data-testid="webhook-secret-reveal">
            {reveal ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => copyToClipboard(data.secret, "Secret copied")} data-testid="webhook-secret-copy">
            <Copy className="size-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="rounded-full h-8" onClick={rotate} disabled={busy} data-testid="webhook-secret-rotate">
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            Rotate
          </Button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500"><Terminal className="size-3" /> Verification snippet</div>
            <div className="inline-flex bg-white border border-slate-200 rounded-full p-0.5">
              {LANGS.map((L) => (
                <button
                  key={L.id}
                  onClick={() => setLang(L.id)}
                  className={`px-3 py-1 text-[11.5px] rounded-full ${lang === L.id ? "bg-slate-900 text-white" : "text-slate-600"}`}
                  data-testid={`webhook-snippet-lang-${L.id}`}
                >
                  {L.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-900 text-slate-100 px-4 py-3 overflow-x-auto">
            <pre className="text-[11.5px] leading-relaxed font-mono whitespace-pre-wrap" data-testid="webhook-snippet">{snippet}</pre>
          </div>
          <p className="text-[11.5px] text-slate-500">
            Allow a {data.tolerance_seconds}s clock-drift tolerance. The signed input is <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">{"<unix-ts>.<raw-body>"}</code>.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
